import { useQuery } from '@tanstack/react-query';
import { organizationSettingsApi } from '@/lib/api/organization-settings.api';
import { queryKeys } from '../query-keys';
import { useOrganizationStore } from '@/store/organization.store';
import type { OrganizationSettings } from '@scrambl/shared';
import { getDefaultOrganizationSettings, getEffectiveSettings } from '@scrambl/shared';

interface OrganizationSettingsResponse {
  settings: OrganizationSettings;
  effectiveSettings: OrganizationSettings;
  defaultMaxProjectCount: number | null;
  /**
   * True when the GET was denied (403) and `settings` holds factory defaults
   * rather than the org's real configuration. Display consumers may still
   * render from defaults; anything that SAVES settings must not, or it would
   * overwrite the org's real configuration with defaults. The settings
   * mutations enforce this.
   */
  isFallback: boolean;
}

/**
 * Hook to fetch organization settings.
 */
export function useOrganizationSettingsQuery(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();

  return useQuery<OrganizationSettingsResponse>({
    queryKey: queryKeys.organizationSettings.detail(currentOrganization?.id || ''),
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('Organization ID is required');
      }

      // Get organization settings
      let settings: OrganizationSettings = getDefaultOrganizationSettings();
      let defaultMaxProjectCount: number | null = null;
      let isFallback = false;
      try {
        const response = await organizationSettingsApi.getSettings(currentOrganization.id);
        settings = response.settings || settings;
        defaultMaxProjectCount = response.defaultMaxProjectCount ?? null;
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status !== 403) {
          throw error;
        }
        // Denied reads fall back to defaults so display-only consumers
        // (timezone, time format, terminology) keep working, but the result
        // is flagged so editors never treat defaults as the org's real
        // configuration and save over it.
        isFallback = true;
      }

      const effectiveSettings = getEffectiveSettings(settings);

      return {
        settings,
        effectiveSettings,
        defaultMaxProjectCount,
        isFallback,
      };
    },
    enabled: (options?.enabled !== false && !!currentOrganization?.id) ?? false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A fallback result usually comes from a transient 403 (token refresh,
    // guard hiccup); retry soon instead of serving defaults for 5 minutes.
    refetchInterval: query => (query.state.data?.isFallback ? 30 * 1000 : false),
  });
}
